import { useEffect, useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'
import type { AnalyticsEvent } from '@/api/types/readiness'
import { DEFAULT_INDEX_ID, listIndexes } from '@/lib/indexes/registry'
import { useAuthStore } from '@/stores/authStore'
import { useReadinessStore } from '@/stores/readinessStore'
import { isLocked, PROFILE_PARAM, useProfileStore } from '@/stores/profileStore'

// ── Profile in the URL (Iteration 6 POC §1.1, Note 2) ───────────────────────
// The profile rides along as `?profile=` on every in-module URL, so a copied
// link opens in the same profile. The router's links don't know about it:
// after each navigation `ProfileUrlSync` writes it back with
// `history.replaceState` (Back is not polluted, router state is kept). The
// login route sits outside the Layout, so signing out drops it.

// The index a Readiness page shows: the URL's `?index=`, else the stored
// selection. With `index_switch` locked (POC §3.2) only CSA QRI is reachable,
// whatever the URL or storage say.
export function resolveIndexId(paramIndex: string | null, selected: string, indexLocked: boolean): string {
  if (indexLocked) return DEFAULT_INDEX_ID
  const ids = listIndexes().map((i) => i.id)
  if (paramIndex && ids.includes(paramIndex)) return paramIndex
  return ids.includes(selected) ? selected : ids[0]
}

// Analytics outside a page's own scope (account menu, locked items): the
// event lands in the current org's scope for the selected index.
export function trackReadiness(ev: AnalyticsEvent) {
  const orgId = useAuthStore.getState().orgId ?? ''
  const s = useReadinessStore.getState()
  const indexId = resolveIndexId(null, s.selectedIndexId, isLocked(useProfileStore.getState(), 'index_switch'))
  s.track(orgId, indexId, ev)
}

export function ProfileUrlSync() {
  const location = useLocation()
  const profile = useProfileStore((s) => s.profile)
  const indexLocked = useProfileStore((s) => isLocked(s, 'index_switch'))
  const pending = useProfileStore((s) => s.pending)
  const clearPending = useProfileStore((s) => s.clearPending)
  useLayoutEffect(() => {
    const url = new URL(window.location.href)
    const sp = url.searchParams
    let changed = false
    if (sp.get(PROFILE_PARAM) !== profile) {
      sp.set(PROFILE_PARAM, profile)
      changed = true
    }
    // A non-CSA index in the URL falls back to CSA QRI while the switch is locked.
    const ix = sp.get('index')
    if (indexLocked && ix && ix !== DEFAULT_INDEX_ID) {
      sp.set('index', DEFAULT_INDEX_ID)
      changed = true
    }
    if (changed) window.history.replaceState(window.history.state, '', `${url.pathname}?${sp.toString()}${url.hash}`)
  }, [location.pathname, location.search, location.hash, location.key, profile, indexLocked])
  useEffect(() => {
    if (!pending) return
    trackReadiness({ name: 'readiness.profile_changed', ...pending })
    clearPending()
  }, [pending, clearPending])
  return null
}
